import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Text, Grid, Button, Input, VStack, HStack,
  Link, Textarea, Select,
} from '@chakra-ui/react';
import { keyframes as chakraKeyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import Marquee from '../components/ui/Marquee';
import {
  VideoCard, ArticleCardLarge, ArticleCardMini,
  ProductCard, SectionHeader, TagBadge,
} from '../components/ui/Cards';
import { useApp } from '../hooks/useApp';
import { TESTIMONIALS } from '../data/seedData';

const fadeUp = chakraKeyframes`
  from { opacity: 0; transform: translateY(22px); }
  to { opacity: 1; transform: translateY(0); }
`;

export default function HomePage() {
  const navigate = useNavigate();
  const { articles, videos, products, subscribe, submitContact, showToast } = useApp();
  const [subName, setSubName] = useState('');
  const [subEmail, setSubEmail] = useState('');
  const [contact, setContact] = useState({ name: '', email: '', subject: '', category: 'General', message: '' });

  const handleSubscribe = async () => {
    await subscribe(subName, subEmail);
    setSubName('');
    setSubEmail('');
  };

  const handleContact = async () => {
    if (!contact.name || !contact.email || !contact.message) {
      showToast('Please fill all required fields.', '⚠️', 'warning');
      return;
    }
    await submitContact(contact);
    setContact({ name: '', email: '', subject: '', category: 'General', message: '' });
  };

  const publishedArticles = articles.filter(a => a.status === 'published');
  const featuredArticle = publishedArticles[0];
  const sideArticles = publishedArticles.slice(1, 4);

  return (
    <Box>
      {/* HERO */}
      <Box
        as="section"
        minH="100vh"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="flex-start"
        px="5%"
        pt="130px"
        pb="80px"
        position="relative"
        overflow="hidden"
      >
        {/* Orbs */}
        <Box
          position="absolute"
          w="600px"
          h="600px"
          bg="rgba(0,195,255,0.07)"
          borderRadius="50%"
          filter="blur(110px)"
          top="-120px"
          right="-80px"
          pointerEvents="none"
        />
        <Box
          position="absolute"
          w="450px"
          h="450px"
          bg="rgba(71,200,255,0.05)"
          borderRadius="50%"
          filter="blur(110px)"
          bottom="-60px"
          left="15%"
          pointerEvents="none"
        />

        <Box position="relative" zIndex={1} w="100%">
          <Box
            display="inline-flex"
            alignItems="center"
            gap={2}
            bg="rgba(0,195,255,0.07)"
            border="1px solid rgba(0,195,255,0.25)"
            color="#00c3ff"
            fontSize="0.78rem"
            fontWeight={600}
            px={3.5}
            py={1.5}
            borderRadius="full"
            mb={6}
            animation={`${fadeUp} 0.6s both`}
          >
            <Box w="6px" h="6px" bg="#00c3ff" borderRadius="50%" />
            India's Honest Tech Voice
          </Box>

          <Text
            as="h1"
            fontFamily="'Fraunces', serif"
            fontSize={{ base: '2.8rem', md: '4rem', lg: '6rem' }}
            fontWeight={900}
            letterSpacing="-0.03em"
            maxW="860px"
            lineHeight={1.1}
            animation={`${fadeUp} 0.7s 0.1s both`}
          >
            Your <Text as="em" fontStyle="normal" color="#00c3ff">Tech Guide</Text>
            <br />for the Real World
          </Text>

          <Text
            color="#6b7fa0"
            maxW="520px"
            mt={5}
            mb={9}
            fontWeight={300}
            fontSize="1.1rem"
            animation={`${fadeUp} 0.7s 0.2s both`}
          >
            Unbiased reviews, buying guides, and the best tech deals — handpicked for Indian users. No fluff, just real talk.
          </Text>

          <Flex gap={3.5} flexWrap="wrap" animation={`${fadeUp} 0.7s 0.3s both`}>
            <Button
              bg="#00c3ff"
              color="#000"
              fontWeight={700}
              px={6}
              py={3}
              borderRadius="8px"
              _hover={{ transform: 'translateY(-2px)', boxShadow: '0 12px 32px rgba(0,195,255,0.4)' }}
              onClick={() => navigate('/articles')}
            >
              📖 Read Blog
            </Button>
            <Link
              href="https://www.youtube.com/@techbheekaran"
              target="_blank"
              display="inline-flex"
              alignItems="center"
              gap={2}
              bg="transparent"
              color="#f0f0f5"
              border="1px solid rgba(255,255,255,0.07)"
              fontWeight={600}
              px={5}
              py={2.5}
              borderRadius="8px"
              transition="all 0.2s"
              _hover={{ borderColor: '#00c3ff', bg: 'rgba(0,195,255,0.05)', color: '#00c3ff', textDecoration: 'none' }}
            >
              ▶ Watch on YouTube
            </Link>
          </Flex>

          <Flex gap={9} mt={14} flexWrap="wrap" animation={`${fadeUp} 0.7s 0.4s both`}>
            {[
              { num: '1K+', label: 'YouTube Subscribers' },
              { num: '50+', label: 'Videos Published' },
              { num: '10+', label: 'Products Reviewed' },
              { num: '100%', label: 'Unbiased Reviews' },
            ].map((stat) => (
              <Box key={stat.label}>
                <Text fontFamily="'Fraunces', serif" fontSize="1.8rem" fontWeight={900} color="#00c3ff">
                  {stat.num}
                </Text>
                <Text fontSize="0.78rem" color="#6b7fa0">{stat.label}</Text>
              </Box>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* MARQUEE */}
      <Marquee />

      {/* VIDEOS SECTION */}
      <Box as="section" py="90px" px="5%">
        <Flex align="flex-end" justify="space-between" flexWrap="wrap" gap={3.5} mb={11}>
          <SectionHeader
            label="Latest Content"
            title='Fresh from <em style="font-style:normal;color:#00c3ff">YouTube</em>'
            desc="My latest reviews, comparisons, and how-to videos."
          />
          <Link
            href="https://www.youtube.com/@techbheekaran"
            target="_blank"
            display="inline-flex"
            alignItems="center"
            bg="transparent"
            color="#f0f0f5"
            border="1px solid rgba(255,255,255,0.07)"
            fontWeight={600}
            fontSize="0.88rem"
            px={4.5}
            py={2}
            borderRadius="8px"
            transition="all 0.2s"
            _hover={{ borderColor: '#00c3ff', color: '#00c3ff', textDecoration: 'none' }}
          >
            View All →
          </Link>
        </Flex>
        <Grid templateColumns={{ base: '1fr', sm: 'repeat(2,1fr)', lg: 'repeat(3,1fr)' }} gap={5.5}>
          {videos.slice(0, 6).map(video => (
            <VideoCard
              key={video.id}
              video={video}
              onClick={() => window.open(video.url, '_blank')}
            />
          ))}
        </Grid>
      </Box>

      {/* BLOG SECTION */}
      <Box
        as="section"
        py="90px"
        px="5%"
        bg="#0a0f1a"
        borderTop="1px solid rgba(255,255,255,0.07)"
      >
        <Flex align="flex-end" justify="space-between" flexWrap="wrap" gap={3.5} mb={11}>
          <SectionHeader
            label="Blog & Articles"
            title='Deep Dives & <em style="font-style:normal;color:#00c3ff">Guides</em>'
            desc="Long-form buying guides and expert opinions."
          />
          <Button
            variant="ghost"
            onClick={() => navigate('/articles')}
            bg="transparent"
            color="#f0f0f5"
            border="1px solid rgba(255,255,255,0.07)"
            fontWeight={600}
            fontSize="0.88rem"
            px={4.5}
            py={2}
            borderRadius="8px"
            _hover={{ borderColor: '#00c3ff', color: '#00c3ff' }}
          >
            All Articles →
          </Button>
        </Flex>

        <Grid templateColumns={{ base: '1fr', lg: '1.6fr 1fr' }} gap={5.5} mb={14}>
          {featuredArticle && (
            <ArticleCardLarge
              article={featuredArticle}
              onClick={() => navigate(`/articles/${featuredArticle.id}`)}
            />
          )}
          <VStack gap={4.5} align="stretch">
            {sideArticles.map(article => (
              <ArticleCardMini
                key={article.id}
                article={article}
                onClick={() => navigate(`/articles/${article.id}`)}
              />
            ))}
          </VStack>
        </Grid>

        {/* Testimonials */}
        <Box>
          <Flex align="center" gap={2.5} mb={2.5}>
            <Box w="26px" h="1px" bg="#00c3ff" />
            <Text fontSize="0.72rem" fontWeight={600} letterSpacing="0.15em" textTransform="uppercase" color="#00c3ff">
              Community Love
            </Text>
          </Flex>
          <Text fontFamily="'Fraunces', serif" fontSize="1.4rem" fontWeight={700} mb={6}>
            What Subscribers <Text as="span" color="#00c3ff">Say</Text>
          </Text>
          <Flex gap={4} overflowX="auto" pb={2} css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
            {TESTIMONIALS.map((t, i) => (
              <Box
                key={i}
                minW={{ base: '280px', md: '300px' }}
                bg="#0e1520"
                border="1px solid rgba(255,255,255,0.07)"
                borderRadius="14px"
                p={5}
                flexShrink={0}
              >
                <Text color="#00c3ff" fontSize="1rem" mb={3}>{'★'.repeat(t.star)}</Text>
                <Text color="#6b7fa0" fontSize="0.88rem" lineHeight={1.7} mb={4}>"{t.text}"</Text>
                <Flex align="center" gap={3}>
                  <Text fontSize="1.5rem">{t.av}</Text>
                  <Box>
                    <Text fontWeight={600} fontSize="0.88rem">{t.name}</Text>
                    <Text color="#6b7fa0" fontSize="0.75rem">{t.sub}</Text>
                  </Box>
                </Flex>
              </Box>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* DEALS SECTION */}
      <Box as="section" py="90px" px="5%" borderTop="1px solid rgba(255,255,255,0.07)">
        <Flex align="flex-end" justify="space-between" flexWrap="wrap" gap={3.5} mb={11}>
          <SectionHeader
            label="Affiliate Picks"
            title='My Recommended <em style="font-style:normal;color:#00c3ff">Gear</em>'
            desc="Every product I personally tested. Affiliate links support the channel — no extra cost to you!"
          />
          <Box
            bg="rgba(0,195,255,0.07)"
            border="1px solid rgba(0,195,255,0.18)"
            borderRadius="8px"
            px={3}
            py={2}
            fontSize="0.75rem"
            color="#6b7fa0"
          >
            🔗 Affiliate Disclosure
          </Box>
        </Flex>
        <Grid templateColumns={{ base: '1fr', sm: 'repeat(2,1fr)', lg: 'repeat(3,1fr)' }} gap={5.5}>
          {products.slice(0, 6).map(p => (
            <ProductCard key={p.id} product={p} />
          ))}
        </Grid>
        <Flex justify="center" mt={8}>
          <Button
            bg="#00c3ff"
            color="#000"
            fontWeight={700}
            px={6}
            py={3}
            borderRadius="8px"
            _hover={{ transform: 'translateY(-2px)', boxShadow: '0 12px 32px rgba(0,195,255,0.4)' }}
            onClick={() => navigate('/deals')}
          >
            View All Deals →
          </Button>
        </Flex>
      </Box>

      {/* SUBSCRIBE SECTION */}
      <Box id="subscribe" as="section" py="80px" px="5%">
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={10} maxW="900px" mx="auto">
          <Box>
            <Flex align="center" gap={2.5} mb={2.5}>
              <Box w="26px" h="1px" bg="#00c3ff" />
              <Text fontSize="0.72rem" fontWeight={600} letterSpacing="0.15em" textTransform="uppercase" color="#00c3ff">
                Stay Updated
              </Text>
            </Flex>
            <Text fontFamily="'Fraunces', serif" fontSize={{ base: '1.8rem', md: '2.4rem' }} fontWeight={900} letterSpacing="-0.02em" mb={3}>
              Never Miss a<br /><Text as="span" color="#00c3ff">Deal or Review</Text>
            </Text>
            <Text color="#6b7fa0" fontWeight={300} mb={5}>
              Weekly tech deals, exclusive tips, and early access to my reviews — straight to your inbox.
            </Text>
            <VStack align="flex-start" gap={2.5} mb={5}>
              {[
                'Weekly curated tech deals for India',
                'Exclusive subscriber-only content',
                'Early video notifications',
                'Free buying guides & PDFs',
              ].map(perk => (
                <Flex key={perk} align="center" gap={3}>
                  <Box w="6px" h="6px" bg="#00c3ff" borderRadius="50%" flexShrink={0} />
                  <Text color="#6b7fa0" fontSize="0.88rem">{perk}</Text>
                </Flex>
              ))}
            </VStack>
            <Link
              href="https://www.youtube.com/@techbheekaran"
              target="_blank"
              display="inline-flex"
              alignItems="center"
              gap={2}
              bg="#ff0000"
              color="white"
              fontWeight={700}
              fontSize="0.9rem"
              px={5}
              py={2.5}
              borderRadius="8px"
              transition="all 0.2s"
              _hover={{ bg: '#cc0000', textDecoration: 'none' }}
            >
              <svg width="20" height="15" viewBox="0 0 22 16" fill="none">
                <path d="M21.5 2.5C21.24 1.52 20.48.76 19.5.5 17.75 0 11 0 11 0S4.25 0 2.5.5C1.52.76.76 1.52.5 2.5 0 4.25 0 8 0 8s0 3.75.5 5.5c.26.98 1.02 1.74 2 2C4.25 16 11 16 11 16s6.75 0 8.5-.5c.98-.26 1.74-1.02 2-2C22 11.75 22 8 22 8s0-3.75-.5-5.5zM8.75 11.5V4.5l5.75 3.5-5.75 3.5z" fill="white" />
              </svg>
              Subscribe on YouTube
            </Link>
          </Box>
          <Box
            bg="#0a0f1a"
            border="1px solid rgba(255,255,255,0.07)"
            borderRadius="16px"
            p={7}
          >
            <VStack gap={3.5}>
              <Input
                placeholder="Your Name"
                value={subName}
                onChange={e => setSubName(e.target.value)}
                bg="#0e1520"
                border="1px solid rgba(255,255,255,0.07)"
                color="#f0f0f5"
                borderRadius="8px"
                _placeholder={{ color: '#6b7fa0' }}
                _focus={{ borderColor: '#00c3ff', boxShadow: '0 0 0 1px #00c3ff' }}
              />
              <Input
                placeholder="Your Email Address"
                type="email"
                value={subEmail}
                onChange={e => setSubEmail(e.target.value)}
                bg="#0e1520"
                border="1px solid rgba(255,255,255,0.07)"
                color="#f0f0f5"
                borderRadius="8px"
                _placeholder={{ color: '#6b7fa0' }}
                _focus={{ borderColor: '#00c3ff', boxShadow: '0 0 0 1px #00c3ff' }}
                onKeyDown={e => e.key === 'Enter' && handleSubscribe()}
              />
              <Button
                w="100%"
                bg="#00c3ff"
                color="#000"
                fontWeight={700}
                py={3}
                borderRadius="8px"
                _hover={{ transform: 'translateY(-2px)', boxShadow: '0 12px 32px rgba(0,195,255,0.4)' }}
                onClick={handleSubscribe}
              >
                ✉️ Join the Newsletter
              </Button>
              <Text color="#6b7fa0" fontSize="0.78rem" textAlign="center">
                Join 5,000+ subscribers. Unsubscribe anytime.
              </Text>
            </VStack>
          </Box>
        </Grid>
      </Box>

      {/* CONTACT SECTION */}
      <Box id="contact" as="section" py="90px" px="5%" borderTop="1px solid rgba(255,255,255,0.07)">
        <SectionHeader
          label="Get in Touch"
          title={'Let\'s <em style="font-style:normal;color:#00c3ff">Connect</em>'}
          desc="For collaborations, product reviews, sponsorships, or just to say hello."
        />
        <Grid templateColumns={{ base: '1fr', md: '1fr 1.5fr' }} gap={10} mt={10}>
          <Box>
            <Text fontFamily="'Fraunces', serif" fontSize="1.4rem" fontWeight={700} mb={3}>
              Open to Collaborations 🤝
            </Text>
            <Text color="#6b7fa0" lineHeight={1.8} mb={6}>
              Brand reviews, creator collabs, viewer questions — my inbox is open.
            </Text>
            {[
              { icon: '📺', label: 'YouTube', value: '@techbheekaran' },
              { icon: '📧', label: 'Business Email', value: 'business@techbheekaran.com' },
              { icon: '📸', label: 'Instagram', value: '@techbheekaran' },
              { icon: '🐦', label: 'Twitter / X', value: '@techbheekaran' },
            ].map(method => (
              <Flex key={method.label} align="center" gap={4} mb={4}>
                <Flex
                  w="44px"
                  h="44px"
                  bg="rgba(0,195,255,0.07)"
                  border="1px solid rgba(0,195,255,0.18)"
                  borderRadius="10px"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="1.1rem"
                  flexShrink={0}
                >
                  {method.icon}
                </Flex>
                <Box>
                  <Text fontSize="0.75rem" color="#6b7fa0">{method.label}</Text>
                  <Text fontSize="0.92rem" fontWeight={500}>{method.value}</Text>
                </Box>
              </Flex>
            ))}
          </Box>

          <Box
            bg="#0a0f1a"
            border="1px solid rgba(255,255,255,0.07)"
            borderRadius="16px"
            p={7}
          >
            <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={4} mb={4}>
              <Input
                placeholder="Your Name *"
                value={contact.name}
                onChange={e => setContact(c => ({ ...c, name: e.target.value }))}
                bg="#0e1520"
                border="1px solid rgba(255,255,255,0.07)"
                color="#f0f0f5"
                borderRadius="8px"
                _placeholder={{ color: '#6b7fa0' }}
                _focus={{ borderColor: '#00c3ff', boxShadow: '0 0 0 1px #00c3ff' }}
              />
              <Input
                placeholder="Email Address *"
                type="email"
                value={contact.email}
                onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                bg="#0e1520"
                border="1px solid rgba(255,255,255,0.07)"
                color="#f0f0f5"
                borderRadius="8px"
                _placeholder={{ color: '#6b7fa0' }}
                _focus={{ borderColor: '#00c3ff', boxShadow: '0 0 0 1px #00c3ff' }}
              />
            </Grid>
            <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={4} mb={4}>
              <Input
                placeholder="Subject"
                value={contact.subject}
                onChange={e => setContact(c => ({ ...c, subject: e.target.value }))}
                bg="#0e1520"
                border="1px solid rgba(255,255,255,0.07)"
                color="#f0f0f5"
                borderRadius="8px"
                _placeholder={{ color: '#6b7fa0' }}
                _focus={{ borderColor: '#00c3ff', boxShadow: '0 0 0 1px #00c3ff' }}
              />
              <Select
                value={contact.category}
                onChange={e => setContact(c => ({ ...c, category: e.target.value }))}
                bg="#0e1520"
                border="1px solid rgba(255,255,255,0.07)"
                color="#f0f0f5"
                borderRadius="8px"
                _focus={{ borderColor: '#00c3ff' }}
              >
                {['General', 'Collaboration', 'Sponsorship', 'Product Review', 'Other'].map(opt => (
                  <option key={opt} value={opt} style={{ background: '#0e1520' }}>{opt}</option>
                ))}
              </Select>
            </Grid>
            <Textarea
              placeholder="Your Message *"
              value={contact.message}
              onChange={e => setContact(c => ({ ...c, message: e.target.value }))}
              rows={5}
              bg="#0e1520"
              border="1px solid rgba(255,255,255,0.07)"
              color="#f0f0f5"
              borderRadius="8px"
              mb={4}
              _placeholder={{ color: '#6b7fa0' }}
              _focus={{ borderColor: '#00c3ff', boxShadow: '0 0 0 1px #00c3ff' }}
            />
            <Button
              w="100%"
              bg="#00c3ff"
              color="#000"
              fontWeight={700}
              py={3}
              borderRadius="8px"
              _hover={{ transform: 'translateY(-2px)', boxShadow: '0 12px 32px rgba(0,195,255,0.4)' }}
              onClick={handleContact}
            >
              📨 Send Message
            </Button>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
}
